import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus, MagnifyingGlass, Envelope, Play, Pause, Trash,
  CircleNotch, CaretRight, Users, Lightning, CheckCircle,
  ArrowLeft, PencilSimple, Clock, PaperPlaneTilt, X,
  ListNumbers, ArrowsClockwise
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
};

const STATUS_LABELS = {
  draft: 'Borrador',
  active: 'Activa',
  paused: 'Pausada',
  completed: 'Completada',
};

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [processing, setProcessing] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/campaigns`, { withCredentials: true });
      setCampaigns(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/campaigns/process`, {}, { withCredentials: true });
      alert(`Procesado: ${res.data.processed} emails enviados${res.data.errors?.length ? `. Errores: ${res.data.errors.length}` : ''}`);
      fetchCampaigns();
    } catch (err) {
      alert('Error procesando campanas');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta campana y sus inscripciones?')) return;
    try {
      await axios.delete(`${API_URL}/api/b2b/campaigns/${id}`, { withCredentials: true });
      fetchCampaigns();
    } catch (err) {
      alert('Error eliminando');
    }
  };

  const handleToggleStatus = async (campaign) => {
    try {
      if (campaign.status === 'active') {
        await axios.post(`${API_URL}/api/b2b/campaigns/${campaign.id}/pause`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/b2b/campaigns/${campaign.id}/activate`, {}, { withCredentials: true });
      }
      fetchCampaigns();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/prospects')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-prospects">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campanas de Email</h1>
            <p className="text-sm text-slate-500">Secuencias automatizadas para prospeccion B2B</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleProcess}
            disabled={processing}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            data-testid="process-campaigns-btn"
          >
            {processing ? <CircleNotch size={18} className="animate-spin" /> : <ArrowsClockwise size={18} />}
            Procesar Pendientes
          </button>
          <button
            onClick={() => { setSelectedCampaign(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="new-campaign-btn"
          >
            <Plus size={18} /> Nueva Campana
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total Campanas" value={campaigns.length} icon={Envelope} color="blue" />
        <StatCard label="Activas" value={campaigns.filter(c => c.status === 'active').length} icon={Play} color="green" />
        <StatCard label="Prospectos Inscritos" value={campaigns.reduce((s, c) => s + (c.enrolled_count || 0), 0)} icon={Users} color="violet" />
        <StatCard label="Emails Enviados" value={campaigns.reduce((s, c) => s + (c.stats?.total_sent || 0), 0)} icon={PaperPlaneTilt} color="amber" />
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Envelope size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin campanas</h3>
          <p className="text-slate-500 mb-4">Crea tu primera secuencia de emails automatizada</p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Crear Campana
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Envelope size={22} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{c.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status]}`}>
                        {STATUS_LABELS[c.status]}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{c.description || 'Sin descripcion'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6 mr-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-slate-900">{c.steps?.length || 0}</p>
                    <p className="text-xs text-slate-500">Pasos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-violet-600">{c.enrolled_count || 0}</p>
                    <p className="text-xs text-slate-500">Inscritos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{c.completed_count || 0}</p>
                    <p className="text-xs text-slate-500">Completados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-600">{c.stats?.total_sent || 0}</p>
                    <p className="text-xs text-slate-500">Enviados</p>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleStatus(c)}
                    className={`p-2 rounded-lg ${c.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                    title={c.status === 'active' ? 'Pausar' : 'Activar'}
                    data-testid={`toggle-campaign-${c.id}`}
                  >
                    {c.status === 'active' ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  <button
                    onClick={() => { setSelectedCampaign(c); setShowForm(true); }}
                    className="p-2 text-slate-500 hover:bg-slate-50 rounded-lg"
                    data-testid={`edit-campaign-${c.id}`}
                  >
                    <PencilSimple size={18} />
                  </button>
                  <button
                    onClick={() => navigate(`/campaigns/${c.id}`)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    data-testid={`view-campaign-${c.id}`}
                  >
                    <CaretRight size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-campaign-${c.id}`}
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Campaign Form Modal */}
      {showForm && (
        <CampaignFormModal
          campaign={selectedCampaign}
          onClose={() => { setShowForm(false); setSelectedCampaign(null); }}
          onSave={() => { setShowForm(false); setSelectedCampaign(null); fetchCampaigns(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

function CampaignFormModal({ campaign, onClose, onSave }) {
  const [name, setName] = useState(campaign?.name || '');
  const [description, setDescription] = useState(campaign?.description || '');
  const [steps, setSteps] = useState(campaign?.steps || []);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/b2b/templates`, { withCredentials: true })
      .then(res => setTemplates(res.data))
      .catch(() => {});
  }, []);

  const addStep = () => {
    setSteps([...steps, {
      step_number: steps.length + 1,
      delay_days: steps.length === 0 ? 0 : 3,
      subject: '',
      body: '',
      template_id: null
    }]);
  };

  const updateStep = (idx, field, value) => {
    const updated = [...steps];
    updated[idx] = { ...updated[idx], [field]: value };
    setSteps(updated);
  };

  const removeStep = (idx) => {
    const updated = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
    setSteps(updated);
  };

  const applyTemplate = (idx, templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (tmpl) {
      const updated = [...steps];
      updated[idx] = { ...updated[idx], subject: tmpl.subject, body: tmpl.body, template_id: templateId };
      setSteps(updated);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return alert('Nombre requerido');
    setSaving(true);
    try {
      if (campaign) {
        await axios.put(`${API_URL}/api/b2b/campaigns/${campaign.id}`, { name, description, steps }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/b2b/campaigns`, { name, description, steps }, { withCredentials: true });
      }
      onSave();
    } catch (err) {
      alert('Error guardando campana');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 mb-8" data-testid="campaign-form-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-slate-900">
            {campaign ? 'Editar Campana' : 'Nueva Campana'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej: Secuencia de Introduccion"
              data-testid="campaign-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descripcion</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descripcion opcional de la campana"
              data-testid="campaign-desc-input"
            />
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <ListNumbers size={18} /> Pasos de la Secuencia ({steps.length})
              </label>
              <button
                onClick={addStep}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                data-testid="add-step-btn"
              >
                <Plus size={16} /> Agregar Paso
              </button>
            </div>

            {steps.length === 0 && (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-6 text-center text-slate-500">
                <ListNumbers size={32} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Agrega pasos a la secuencia. Cada paso enviara un email despues del tiempo configurado.</p>
              </div>
            )}

            <div className="space-y-3">
              {steps.map((step, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid={`step-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-600">Paso {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      {templates.length > 0 && (
                        <select
                          onChange={e => applyTemplate(idx, e.target.value)}
                          className="text-xs border rounded px-2 py-1"
                          defaultValue=""
                        >
                          <option value="" disabled>Usar plantilla...</option>
                          {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      )}
                      <button onClick={() => removeStep(idx)} className="text-red-400 hover:text-red-600">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        <Clock size={12} className="inline mr-1" />
                        {idx === 0 ? 'Enviar al' : 'Esperar'}
                      </label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          value={step.delay_days}
                          onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)}
                          className="w-16 border rounded px-2 py-1.5 text-sm"
                        />
                        <span className="text-xs text-slate-500">dias</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Asunto</label>
                      <input
                        value={step.subject}
                        onChange={e => updateStep(idx, 'subject', e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                        placeholder="Asunto del email (usa {{company_name}}, {{contact_name}})"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="block text-xs text-slate-500 mb-1">Cuerpo</label>
                    <textarea
                      value={step.body}
                      onChange={e => updateStep(idx, 'body', e.target.value)}
                      rows={3}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      placeholder="Contenido del email... (variables: {{company_name}}, {{contact_name}}, {{services}}, {{city}})"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="save-campaign-btn"
          >
            {saving && <CircleNotch size={16} className="animate-spin" />}
            {campaign ? 'Guardar Cambios' : 'Crear Campana'}
          </button>
        </div>
      </div>
    </div>
  );
}
